"use client"

import Image from "next/image"
import { forwardRef } from "react"
import { ExternalLink, PackageCheck } from "lucide-react"

import { cn } from "@/lib/utils"

export type CuratedProductRoleTone = "danger" | "info" | "success" | "warning"

export interface CuratedProductCardItem {
  id: string
  name: string
  note?: string | undefined
  imageUrl?: string | undefined
  affiliateUrl: string
  roleLabel: string
  roleTone: CuratedProductRoleTone
}

const ROLE_STYLES: Record<CuratedProductRoleTone, string> = {
  danger: "bg-red-50 text-red-700 ring-red-100",
  info: "bg-blue-50 text-blue-700 ring-blue-100",
  success: "bg-green-50 text-green-700 ring-green-100",
  warning: "bg-amber-50 text-amber-700 ring-amber-100",
}

export const CuratedProductPicksCard = forwardRef<HTMLElement, {
  headingId: string
  title: string
  description: string
  items: CuratedProductCardItem[]
  onProductClick: (item: CuratedProductCardItem, index: number) => void
  className?: string
  testId?: string
}>(function CuratedProductPicksCard({
  headingId,
  title,
  description,
  items,
  onProductClick,
  className,
  testId,
}, ref) {
  if (items.length === 0) return null

  return (
    <section
      ref={ref}
      aria-labelledby={headingId}
      data-testid={testId}
      className={cn(
        "overflow-hidden rounded-3xl border border-green-100 bg-white shadow-[0_1px_2px_rgba(16,24,16,0.04),0_18px_48px_-28px_rgba(22,101,52,0.22)]",
        className,
      )}
    >
      <div className="border-b border-green-100 bg-green-50/70 px-4 py-4 md:px-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-white text-green-700 ring-1 ring-green-100">
            <PackageCheck className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h2 id={headingId} className="text-base font-extrabold tracking-tight text-gray-900 md:text-lg">
              {title}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">{description}</p>
          </div>
        </div>
      </div>

      <ul className="grid list-none gap-0 divide-y divide-gray-100 p-0 md:grid-cols-3 md:divide-x md:divide-y-0">
        {items.map((item, index) => (
          <li key={`${item.id}-${index}`} className="list-none">
            <a
              href={item.affiliateUrl}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              data-affiliate="true"
              onClick={() => onProductClick(item, index)}
              className="group flex h-full gap-4 p-4 text-left transition-colors hover:bg-green-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2 md:flex-col md:p-5"
            >
              <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-2xl border border-gray-100 bg-gray-50 md:h-32 md:w-full">
                {item.imageUrl ? (
                  <Image
                    src={item.imageUrl}
                    alt=""
                    fill
                    sizes="(min-width: 768px) 220px, 80px"
                    className="object-contain p-2 transition-transform group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-green-700">
                    <PackageCheck className="h-6 w-6" aria-hidden="true" />
                  </div>
                )}
              </div>

              <div className="flex min-w-0 flex-1 flex-col">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className={cn(
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ring-inset",
                    ROLE_STYLES[item.roleTone],
                  )}>
                    {item.roleLabel}
                  </span>
                </div>
                <h3 className="text-sm font-extrabold leading-snug text-gray-900 group-hover:text-green-800">
                  {item.name}
                </h3>
                {item.note ? <p className="mt-2 text-xs leading-relaxed text-gray-600">{item.note}</p> : null}
                <span className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-2.5 text-sm font-bold text-white shadow-[0_10px_22px_-10px_rgba(22,163,74,0.5)] transition group-hover:-translate-y-0.5">
                  Check price
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </span>
              </div>
            </a>
          </li>
        ))}
      </ul>

      <p className="border-t border-gray-100 px-4 py-3 text-center text-xs text-gray-500 md:px-5">
        As an Amazon Associate, we earn from qualifying purchases.
      </p>
    </section>
  )
})
